// src/components/HSCodeInfiniteScroll.jsx
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { HSCodeList, fetchHSCodeDetail } from "../actions/hscodeActions";
import HSCodeDetail from "./HSCodeDetail";
import { LazyLoading } from "./ui/LazyLoading";
import FilterSlideOver, { AccordionItem } from "./ui/FilterSlideOver";
import { Filter, Search, List, Grid3X3, Percent } from "lucide-react";

const RowSkeleton = () => (
  <div className="grid grid-cols-12 items-center px-3 py-3 animate-pulse">
    <div className="col-span-1 h-4 bg-gray-200 rounded" />
    <div className="col-span-3 h-4 bg-gray-200 rounded" />
    <div className="col-span-3 h-4 bg-gray-200 rounded" />
    <div className="col-span-2 h-4 bg-gray-200 rounded" />
    <div className="col-span-3 h-4 bg-gray-200 rounded" />
  </div>
);

export default function HSCodeInfiniteScroll() {
  const dispatch = useDispatch();
  const { loading, hscodeList, next, error } = useSelector(
    (state) => state.hscodeList
  );

  // paging & data
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  const [loadedHSCodes, setLoadedHSCodes] = useState([]);

  // filters & search
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [profit, setProfit] = useState("");
  const [priority, setPriority] = useState("");
  const [customsDutyRate, setCustomsDutyRate] = useState("");
  const [suq, setSuq] = useState("");
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");

  // overlay
  const [showOverlay, setShowOverlay] = useState(false);

  // view mode
  const [viewMode, setViewMode] = useState("list"); // "list" | "grid"

  // options
  const profitOptions = [
    "",
    "0",
    "1",
    "4",
    "6",
    "9",
    "11",
    "16",
    "18",
    "28",
    "51",
  ];
  const priorityOptions = [
    "",
    "2",
    "4",
    "21",
    "22",
    "23",
    "24",
    "25",
    "26",
    "27",
  ];
  const cdrOptions = ["", "4", "1"];
  const suqOptions = ["", "Kg", "L", "U", "m", "m2"];

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchText);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchText]);

  // reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
    setLoadedHSCodes([]);
  }, [profit, priority, customsDutyRate, suq, search]);

  // fetch page
  useEffect(() => {
    const filters = { profit, priority, customsDutyRate, suq, search };
    dispatch(HSCodeList(currentPage, pageSize, filters));
  }, [
    dispatch,
    currentPage,
    pageSize,
    profit,
    priority,
    customsDutyRate,
    suq,
    search,
  ]);

  // merge pages (dedupe by id)
  useEffect(() => {
    if (!hscodeList) return;
    setLoadedHSCodes((prev) => {
      const merged = currentPage === 1 ? hscodeList : [...prev, ...hscodeList];
      const seen = new Set();
      return merged.filter((item) => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      });
    });
  }, [hscodeList, currentPage]);

  // infinite observer
  const observer = useRef(null);
  const loaderRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && next) setCurrentPage((p) => p + 1);
      });
      if (node) observer.current.observe(node);
    },
    [loading, next]
  );

  const openOverlay = (code) => {
    dispatch(fetchHSCodeDetail(code));
    setShowOverlay(true);
  };
  const handleOverlayClosed = () => setShowOverlay(false);

  const activeFiltersCount = useMemo(
    () =>
      [profit, priority, customsDutyRate, suq, search].filter(Boolean).length,
    [profit, priority, customsDutyRate, suq, search]
  );

  const dutySum = (p, d) => (Number(p) || 0) + (Number(d) || 0);

  return (
    <div dir="rtl" className="mt-20 md:mt-24 px-4 md:px-6 font-vazir">
      {/* Header */}
      <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">فهرست کدهای HS</h1>
          <p className="text-sm text-gray-500 mt-1">
            جستجو، فیلتر و مشاهده جزئیات
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="rounded-xl border p-1 bg-white shadow-sm flex">
            <button
              type="button"
              onClick={() => setViewMode("list")}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm ${
                viewMode === "list"
                  ? "bg-blue-600 text-white"
                  : "hover:bg-gray-50"
              }`}
              title="نمایش لیستی"
            >
              <List className="w-4 h-4" />
              لیست
            </button>
            <button
              type="button"
              onClick={() => setViewMode("grid")}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm ${
                viewMode === "grid"
                  ? "bg-blue-600 text-white"
                  : "hover:bg-gray-50"
              }`}
              title="نمایش شبکه‌ای"
            >
              <Grid3X3 className="w-4 h-4" />
              شبکه
            </button>
          </div>

          <button
            type="button"
            onClick={() => setFiltersOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border bg-white hover:bg-gray-50 transition"
          >
            <Filter className="w-4 h-4" />
            فیلترها
            {activeFiltersCount > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                {activeFiltersCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-5">
        <label className="block text-sm font-medium text-gray-700 text-right mb-2">
          جستجو (کد / نام فارسی / نام انگلیسی):
        </label>
        <div className="relative">
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="عبارت خود را بنویسید…"
            className="w-full rounded-xl border bg-white px-4 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-600"
          />
          <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          خطا: {error}
        </div>
      )}

      {/* LIST VIEW */}
      {viewMode === "list" && (
        <div className="rounded-2xl border bg-white shadow-sm overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-2 px-3 py-3 text-xs text-gray-600 border-b">
            <div className="col-span-1">کد</div>
            <div className="col-span-4">شرح فارسی</div>
            <div className="col-span-3">شرح انگلیسی</div>
            <div className="col-span-2">حقوق ورودی</div>
            <div className="col-span-2">واحد / اولویت</div>
          </div>

          {/* Rows */}
          <div className="divide-y">
            {loadedHSCodes && loadedHSCodes.length > 0 ? (
              loadedHSCodes.map((h) => {
                const duty = dutySum(h.profit, h.customs_duty_rate);
                return (
                  <button
                    key={h.id}
                    onClick={() => openOverlay(h.code)}
                    className="w-full text-right grid grid-cols-12 gap-2 px-3 py-3 hover:bg-gray-50 transition relative"
                    title="مشاهده جزئیات"
                  >
                    {/* left colored duty bar */}
                    <span
                      className="absolute left-0 top-0 h-full w-1 rounded-tr-lg rounded-br-lg"
                      style={{
                        background:
                          duty >= 20
                            ? "linear-gradient(180deg,#ef4444,#f59e0b)"
                            : duty >= 10
                            ? "linear-gradient(180deg,#3b82f6,#22c55e)"
                            : "linear-gradient(180deg,#10b981,#34d399)",
                      }}
                    />
                    <div className="col-span-12 md:col-span-1 font-mono text-gray-800">
                      {h.code}
                    </div>
                    <div className="col-span-12 md:col-span-4">
                      <div className="font-semibold text-gray-900 line-clamp-2">
                        {h.goods_name_fa || "—"}
                      </div>
                    </div>
                    <div className="col-span-12 md:col-span-3">
                      <div className="text-gray-600 text-sm line-clamp-1">
                        {h.goods_name_en || "—"}
                      </div>
                    </div>
                    <div className="col-span-6 md:col-span-2 flex items-center gap-2">
                      <Percent className="w-4 h-4 text-gray-500" />
                      <span className="font-medium">
                        {duty.toLocaleString()}%
                      </span>
                      <span className="text-xs text-gray-400">
                        (سود {h.profit ?? 0} + حقوق {h.customs_duty_rate ?? 0})
                      </span>
                    </div>
                    <div className="col-span-6 md:col-span-2 flex items-center gap-2 justify-start md:justify-end">
                      {h.suq ? (
                        <span className="px-2 py-0.5 rounded-lg bg-indigo-50 text-indigo-700 text-xs">
                          {h.suq}
                        </span>
                      ) : null}
                      {h.priority ? (
                        <span className="px-2 py-0.5 rounded-lg bg-amber-50 text-amber-700 text-xs">
                          اولویت {h.priority}
                        </span>
                      ) : null}
                    </div>
                  </button>
                );
              })
            ) : currentPage === 1 && loading ? (
              Array.from({ length: 8 }).map((_, i) => <RowSkeleton key={i} />)
            ) : (
              <div className="px-3 py-6 text-center text-gray-500">
                نتیجه‌ای یافت نشد.
              </div>
            )}
          </div>

          {/* Infinite loader */}
          {loading && currentPage > 1 && (
            <div className="p-3">
              <LazyLoading />
            </div>
          )}
          <div ref={loaderRef} className="h-6" />
        </div>
      )}

      {/* GRID VIEW (compact, minimal) */}
      {viewMode === "grid" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
          {loadedHSCodes && loadedHSCodes.length > 0 ? (
            loadedHSCodes.map((h) => {
              const duty = dutySum(h.profit, h.customs_duty_rate);
              return (
                <button
                  key={h.id}
                  onClick={() => openOverlay(h.code)}
                  className="group text-right rounded-xl border bg-white shadow-sm overflow-hidden hover:shadow-md transition"
                >
                  <div className="p-4">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-mono text-sm text-gray-800">
                        {h.code}
                      </span>
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs"
                        style={{
                          background:
                            duty >= 20
                              ? "rgba(239,68,68,0.1)"
                              : duty >= 10
                              ? "rgba(59,130,246,0.1)"
                              : "rgba(16,185,129,0.1)",
                          color:
                            duty >= 20
                              ? "#ef4444"
                              : duty >= 10
                              ? "#2563eb"
                              : "#059669",
                        }}
                      >
                        <Percent className="w-3 h-3" />
                        {duty.toLocaleString()}%
                      </span>
                    </div>
                    <div className="mt-3 font-semibold text-gray-900 line-clamp-2">
                      {h.goods_name_fa || "—"}
                    </div>
                    {h.goods_name_en && (
                      <div className="mt-1 text-xs text-gray-500 line-clamp-1">
                        {h.goods_name_en}
                      </div>
                    )}
                    <div className="mt-3 flex items-center gap-2">
                      {h.suq && (
                        <span className="px-2 py-0.5 rounded-lg bg-indigo-50 text-indigo-700 text-xs">
                          {h.suq}
                        </span>
                      )}
                      {h.priority && (
                        <span className="px-2 py-0.5 rounded-lg bg-amber-50 text-amber-700 text-xs">
                          اولویت {h.priority}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          ) : currentPage === 1 && loading ? (
            Array.from({ length: 8 }).map((_, i) => <RowSkeleton key={i} />)
          ) : (
            <div className="col-span-full text-center text-gray-500">
              نتیجه‌ای یافت نشد.
            </div>
          )}
          {/* Infinite loader */}
          {loading && currentPage > 1 && (
            <div className="col-span-full">
              <LazyLoading />
            </div>
          )}
          <div ref={loaderRef} className="h-6 col-span-full" />
        </div>
      )}

      {/* Soft footer loading note */}
      {loading && (
        <p className="mt-3 text-center text-sm text-gray-500">
          در حال بارگذاری…
        </p>
      )}

      {/* Detail overlay */}
      {showOverlay && <HSCodeDetail onClose={handleOverlayClosed} />}

      {/* Slide-Over Filters */}
      <FilterSlideOver
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title="فیلترها"
        badgeCount={activeFiltersCount}
        onClear={() => {
          setProfit("");
          setPriority("");
          setCustomsDutyRate("");
          setSuq("");
          setSearchText("");
        }}
        onApply={() => setFiltersOpen(false)}
        dir="rtl"
      >
        <AccordionItem title="تعرفه و اولویت" defaultOpen>
          <div className="grid grid-cols-1 gap-3">
            <div className="space-y-1">
              <label className="text-sm text-gray-700 block text-right">
                سود بازرگانی
              </label>
              <select
                className="w-full rounded-lg border px-3 py-2 bg-white"
                value={profit}
                onChange={(e) => setProfit(e.target.value)}
              >
                {profitOptions.map((val) => (
                  <option key={val || "all"} value={val}>
                    {val === "" ? "همه" : val}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-sm text-gray-700 block text-right">
                اولویت
              </label>
              <select
                className="w-full rounded-lg border px-3 py-2 bg-white"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              >
                {priorityOptions.map((val) => (
                  <option key={val || "all"} value={val}>
                    {val === "" ? "همه" : val}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </AccordionItem>

        <AccordionItem title="حقوق گمرکی و واحد" defaultOpen>
          <div className="grid grid-cols-1 gap-3">
            <div className="space-y-1">
              <label className="text-sm text-gray-700 block text-right">
                حقوق گمرکی
              </label>
              <select
                className="w-full rounded-lg border px-3 py-2 bg-white"
                value={customsDutyRate}
                onChange={(e) => setCustomsDutyRate(e.target.value)}
              >
                {cdrOptions.map((val) => (
                  <option key={val || "all"} value={val}>
                    {val === "" ? "همه" : val}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-sm text-gray-700 block text-right">
                واحد
              </label>
              <select
                className="w-full rounded-lg border px-3 py-2 bg-white"
                value={suq}
                onChange={(e) => setSuq(e.target.value)}
              >
                {suqOptions.map((val) => (
                  <option key={val || "all"} value={val}>
                    {val === "" ? "همه" : val}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </AccordionItem>

        <AccordionItem title="جستجو" defaultOpen>
          <div className="space-y-2">
            <label className="text-sm text-gray-700 block text-right">
              کد / نام فارسی / نام انگلیسی
            </label>
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="عبارت مورد نظر…"
              className="w-full rounded-lg border px-3 py-2 bg-white"
            />
          </div>
        </AccordionItem>
      </FilterSlideOver>
    </div>
  );
}
